import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, ExternalLink, ArrowRight, Lock, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import portfolioOwnYourCredit from "@/assets/portfolio-ownyourcredit.png";
import portfolioForks247 from "@/assets/portfolio-forks247.png";
import portfolioFreshVision from "@/assets/portfolio-freshvision.png";
import portfolioWeBuildFamily from "@/assets/portfolio-webuildfamily.png";
import PageSEO from "@/components/PageSEO";
import { breadcrumb } from "@/lib/schemas";

const Tools = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tools = [
    {
      title: "Newsletter Generator",
      icon: Bot,
      description: "Generate on-brand newsletters in minutes using your voice, tone, and content standards.",
      link: "https://newsgenerator.marquettabryant.com/"
    }
  ];

  const portfolioApps = [
    {
      title: "Own Your Credit",
      description: "A comprehensive credit repair platform.",
      link: "https://app.ownyourcredit.org/",
      image: portfolioOwnYourCredit
    },
    {
      title: "Forks 247",
      description: "Kitchen and logistics management system.",
      link: "https://app.forks247.com/kitchen",
      image: portfolioForks247
    }
  ];

  const portfolioWebs = [
    {
      title: "Fresh Vision Realty",
      description: "Modern brokerage presence and lead capture.",
      link: "https://www.freshvisionrealty.com/",
      image: portfolioFreshVision
    },
    {
      title: "We Build Family",
      description: "Community organization platform.",
      link: "https://webuildfamily.com/",
      image: portfolioWeBuildFamily
    }
  ];

  const labItems = [
    {
      title: "Custom GPTs",
      description: "AI assistants tailored to your business workflows.",
      icon: Bot
    },
    {
      title: "Agent Clones",
      description: "Digital replicas that handle tasks in your voice.",
      icon: Sparkles
    }
  ];

  const handleNotify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("subscribe", {
        body: { email: email.trim() },
      });

      if (error) {
        throw error;
      }

      if (data?.message === "Already subscribed") {
        toast.info("You're already on the list!");
      } else {
        toast.success("Welcome to the inner circle.");
        setEmail("");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fffcf2]">
      <PageSEO
        title="Founder Resources & Market Intel | Fresh Vision Academy"
        description="Free tools, market intelligence, and founder resources for real estate agents and entrepreneurs. AI workflows, deal analysis templates, resource library."
        canonical="https://marquettabryant.com/academy/tools"
        jsonLd={[breadcrumb([{name:"Home",path:"/"},{name:"Academy",path:"/academy"},{name:"Tools",path:"/academy/tools"}])]}
      />
      <Header />
      <main id="main-content">
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-[#2e4053] mb-4">
            The AI Toolkit
          </h1>
          <p className="text-xl md:text-2xl text-[#2e4053]/80 mb-6">
            Tools to automate your empire.
          </p>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            {tools.map((tool, index) => (
              <div
                key={index}
                className="relative group rounded-2xl p-8 backdrop-blur-md bg-white/60 border border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: "linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.4) 100%)"
                }}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-[#ff69b4] flex items-center justify-center mb-6 shadow-md">
                  <tool.icon className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-xl font-bold text-[#2e4053] mb-3">
                  {tool.title}
                </h2>
                <p className="text-[#2e4053]/70 mb-6 leading-relaxed">
                  {tool.description}
                </p>
                <a href={tool.link} target="_blank" rel="noopener noreferrer">
                  <Button variant="premium" className="w-full group/btn">
                    Launch App
                    <ExternalLink className="w-4 h-4 ml-2 group-hover/btn:translate-x-0.5 transition-transform" />
                  </Button>
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-[#2e4053]/60 mt-10">
            More tools coming soon — including custom GPTs and premium automations.
          </p>
        </div>
      </section>

      {/* Academy Opt-in Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#2e4053] to-[#1a2530]">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Want to build these yourself?
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-4">
              The tools you see here are powered by systems, not shortcuts.
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Inside the Academy, I teach how to design, build, and control the same infrastructure behind these apps.
            </p>
            <p className="text-white/70 mb-10">
              No hustle. No gatekeepers. No dependency on platforms you don't own.
            </p>

            <Link to="/academy">
              <Button size="lg" variant="premium" className="group mb-4">
                Join the Classes
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <p className="text-sm text-white/50 mb-12">
              Learn the system. Then decide what you want to automate.
            </p>

            {/* Email Capture */}
            <div className="pt-8 border-t border-white/10">
              <p className="text-white/70 mb-4">
                Get notified when new tools or classes drop.
              </p>
              <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <label htmlFor="notify-email" className="sr-only">Email address</label>
                <Input
                  id="notify-email"
                  type="email"
                  aria-label="Email address"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-primary"
                  required
                  disabled={isSubmitting}
                />
                <Button 
                  type="submit" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Notify Me"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-16 md:py-24 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2e4053] mb-4">
              Built by Marquetta
            </h2>
            <p className="text-lg text-[#2e4053]/70">
              Real projects. Real results. Real infrastructure.
            </p>
          </div>

          {/* Custom Applications */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold text-[#2e4053] mb-6 text-center">
              Custom Applications
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {portfolioApps.map((project, index) => (
                <div
                  key={index}
                  className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#2e4053] to-[#1a2530] text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={project.title} 
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="text-lg font-bold mb-2">{project.title}</h4>
                    <p className="text-white/70 mb-4 text-sm">{project.description}</p>
                    <a href={project.link} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10 hover:text-white">
                        Visit Project
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Web Platforms */}
          <div>
            <h3 className="text-xl font-semibold text-[#2e4053] mb-6 text-center">
              Web Platforms
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {portfolioWebs.map((project, index) => (
                <div
                  key={index}
                  className="rounded-2xl overflow-hidden backdrop-blur-md bg-white/80 border border-[#2e4053]/10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={project.title} 
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="text-lg font-bold text-[#2e4053] mb-2">{project.title}</h4>
                    <p className="text-[#2e4053]/70 mb-4 text-sm">{project.description}</p>
                    <a href={project.link} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm">
                        Visit Project
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Lab Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2e4053] mb-4">
              The Lab
            </h2>
            <p className="text-lg text-[#2e4053]/70">
              Coming Soon
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {labItems.map((item, index) => (
              <div
                key={index}
                className="relative rounded-2xl p-6 backdrop-blur-md bg-white/40 border border-white/30 shadow-md opacity-70"
                style={{
                  background: "linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 100%)"
                }}
              >
                <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#2e4053]/10 text-[#2e4053]/60 text-xs font-medium">
                  <Lock className="w-3 h-3" />
                  Coming Soon
                </div>

                <div className="w-12 h-12 rounded-xl bg-[#2e4053]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#2e4053]/50" />
                </div>
                <h4 className="text-lg font-bold text-[#2e4053]/70 mb-2">{item.title}</h4>
                <p className="text-[#2e4053]/50 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default Tools;
